import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../providers/AuthProvider';
import { AuthControllerApi } from '../../api-generated';
import { apiConfig } from '../../api-generated/apiConfig';

const BASE = apiConfig.basePath;
const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

/** Turn a raw error response body into a clean, human-readable message. */
function parseAuthError(raw: string, status: number): string {
  let msg = (raw || '').trim();
  // Try to pull a message out of a JSON error body (Spring / Cognito shapes)
  try {
    const j = JSON.parse(msg);
    msg = j.message || j.error_description || j.error || j.errorMessage || msg;
  } catch {
    /* not JSON — use as-is */
  }
  const lower = msg.toLowerCase();
  // Friendly mappings for common cases
  if (lower.includes('password') && (lower.includes('policy') || lower.includes('did not conform') || lower.includes('constraint'))) {
    return 'Password does not meet requirements: at least 8 characters with uppercase, lowercase, and a number.';
  }
  if (lower.includes('usernameexists') || lower.includes('already exists') || lower.includes('user account already')) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (lower.includes('invalidparameter') && lower.includes('email')) {
    return 'Please enter a valid email address.';
  }
  // If we still have a huge/blob string (stack trace, HTML), don't dump it
  if (!msg || msg.length > 300 || lower.includes('<html') || lower.includes('exception')) {
    return status >= 500 ? 'Something went wrong. Please try again.' : 'Request failed. Please check your details and try again.';
  }
  return msg;
}

/**
 * Unwrap an error thrown by the generated API client. On an HTTP error the client throws a
 * `ResponseError` carrying the raw `Response`, so we read its body and run it through the same
 * friendly-message parser used elsewhere. Network/other errors fall back to `fallback`.
 */
async function extractApiError(err: any, fallback: string): Promise<string> {
  const res: Response | undefined = err?.response;
  if (res) {
    let raw = '';
    try {
      raw = await res.text();
    } catch {
      /* body already consumed or unreadable */
    }
    return parseAuthError(raw, res.status);
  }
  return err?.message || fallback;
}

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState<'login' | 'signup' | 'forgot' | 'reset'>('login');
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED_EMAIL_KEY) || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => !!localStorage.getItem(REMEMBERED_EMAIL_KEY),
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      // Remember the email for next time (email only — never the password).
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, firstName, lastName }),
      });
      if (!res.ok) {
        const raw = await res.text();
        throw new Error(parseAuthError(raw, res.status));
      }
      setSuccess('Account created! An administrator will review and approve your account.');
      setMode('login');
      setPassword('');
      setFirstName('');
      setLastName('');
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setSuccess('');
  };

  // Step 1 of reset: ask Cognito to email a reset code. The backend responds generically
  // (never reveals whether the account exists), so we always advance to the code-entry step.
  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const authApi = new AuthControllerApi(apiConfig);
      await authApi.forgotPassword({ requestBody: { email } });
      setSuccess('If an account exists for that email, a reset code has been sent. Check your inbox.');
      setMode('reset');
    } catch (err: any) {
      setError(await extractApiError(err, 'Could not start password reset'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2 of reset: submit the emailed code plus the new password.
  const handleResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const authApi = new AuthControllerApi(apiConfig);
      await authApi.confirmForgotPassword({
        requestBody: { email, code: resetCode.trim(), newPassword: password },
      });
      setSuccess('Your password has been reset. You can now sign in.');
      setMode('login');
      setPassword('');
      setResetCode('');
    } catch (err: any) {
      setError(await extractApiError(err, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  const goToForgot = () => {
    setMode('forgot');
    setError('');
    setSuccess('');
    setPassword('');
  };

  const backToLogin = () => {
    setMode('login');
    setError('');
    setSuccess('');
    setResetCode('');
  };

  const titles: Record<typeof mode, { title: string; subtitle: string }> = {
    login: { title: 'Welcome Back', subtitle: 'Sign in to continue to TrueOne' },
    signup: { title: 'Create Account', subtitle: 'Sign up to request access' },
    forgot: { title: 'Reset Password', subtitle: 'Enter your email to receive a reset code' },
    reset: { title: 'Reset Password', subtitle: 'Enter the code we emailed you and a new password' },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          boxShadow: theme.shadows[8],
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/logo.svg"
                alt="TrueOne"
                sx={{ height: 40, mb: 1 }}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
              <Typography variant="h4" fontWeight={600} color="text.primary">
                {titles[mode].title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {titles[mode].subtitle}
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <form
              onSubmit={
                mode === 'login'
                  ? handleLogin
                  : mode === 'signup'
                    ? handleSignup
                    : mode === 'forgot'
                      ? handleForgotRequest
                      : handleResetConfirm
              }
            >
              <Stack spacing={2.5}>
                {mode === 'signup' && (
                  <>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      fullWidth
                      required
                    />
                  </>
                )}
                {/* Email is shown for every mode except the final reset step,
                    where the address is already known from the previous step. */}
                {mode !== 'reset' && (
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    autoFocus={mode === 'login'}
                    inputProps={{ 'data-testid': 'login-email' }}
                  />
                )}
                {mode === 'reset' && (
                  <TextField
                    label="Reset Code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    helperText="Enter the code we emailed you"
                    inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
                  />
                )}
                {/* Password field: present on login, signup, and the reset step
                    (as the new password) — but not on the forgot email-entry step. */}
                {mode !== 'forgot' && (
                  <TextField
                    label={mode === 'reset' ? 'New Password' : 'Password'}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    helperText={
                      mode === 'signup' || mode === 'reset'
                        ? 'Minimum 8 characters with uppercase, lowercase, and number'
                        : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((s) => !s)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ 'data-testid': 'login-password' }}
                  />
                )}
                {mode === 'login' && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                      }
                      label="Remember me"
                    />
                    <Link component="button" type="button" variant="body2" onClick={goToForgot}>
                      Forgot password?
                    </Link>
                  </Stack>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{ py: 1.2 }}
                  data-testid="login-submit"
                >
                  {loading
                    ? mode === 'login'
                      ? 'Signing in...'
                      : mode === 'signup'
                        ? 'Creating account...'
                        : mode === 'forgot'
                          ? 'Sending code...'
                          : 'Resetting...'
                    : mode === 'login'
                      ? 'Sign In'
                      : mode === 'signup'
                        ? 'Sign Up'
                        : mode === 'forgot'
                          ? 'Send Reset Code'
                          : 'Reset Password'}
                </Button>
                {mode === 'reset' && (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={handleForgotRequest}
                    disabled={loading}
                  >
                    Resend code
                  </Button>
                )}
              </Stack>
            </form>

            <Typography variant="body2" color="text.secondary" align="center">
              {mode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <Link component="button" variant="body2" onClick={switchMode}>
                    Sign up
                  </Link>
                </>
              ) : mode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <Link component="button" variant="body2" onClick={switchMode}>
                    Sign in
                  </Link>
                </>
              ) : (
                <>
                  Remembered it?{' '}
                  <Link component="button" variant="body2" onClick={backToLogin}>
                    Back to sign in
                  </Link>
                </>
              )}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
